import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Template.css";
import { getDataDetail, getDataList } from "../../../../api/dataApi";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const normalizeFileItem = (file) => {
  if (!file || typeof file !== "object") return null;

  return {
    id:
      file.certificateFileId ||
      file.certificateDocFileId ||
      file.delegateFileId ||
      file.delegateDocFileId ||
      file.fileId ||
      file.id ||
      null,
    name:
      file.fileName ||
      file.originalFileName ||
      file.originalName ||
      file.name ||
      file.savedName ||
      "첨부파일",
    size: file.fileSize || file.size || "",
    url: file.fileUrl || file.url || file.downloadUrl || file.filePath || "",
  };
};

const getFiles = (item = {}) => {
  const fileList = [];

  const possibleArrays = [
    item.files,
    item.fileList,
    item.fileResponses,
    item.certificateFiles,
    item.certificateDocFiles,
    item.delegateFiles,
    item.delegateDocFiles,
    item.attachments,
  ];

  possibleArrays.forEach((array) => {
    if (Array.isArray(array)) {
      fileList.push(...array);
    }
  });

  const possibleSingleFiles = [
    item.file,
    item.attachment,
    item.attachFile,
    item.fileResponse,
  ];

  possibleSingleFiles.forEach((fileItem) => {
    if (fileItem && typeof fileItem === "object") {
      fileList.push(fileItem);
    }
  });

  if (item.fileName || item.originalFileName || item.fileUrl) {
    fileList.push({
      fileName: item.fileName || item.originalFileName,
      fileSize: item.fileSize,
      fileUrl: item.fileUrl,
    });
  }

  const normalizedFiles = fileList.map(normalizeFileItem).filter(Boolean);

  return normalizedFiles.filter((file, index, array) => {
    const key = file.id || file.url || file.name;

    return (
      array.findIndex((target) => {
        const targetKey = target.id || target.url || target.name;
        return targetKey === key;
      }) === index
    );
  });
};

const getPagedArray = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.content)) return result.content;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.content)) return result.data.content;

  return [];
};

const normalizeListItem = (item) => ({
  id:
    item.id ||
    item.certificateDocId ||
    item.delegateDocId ||
    item.docId,
  title: item.title || "",
  rawDate: item.date || item.createdAt || item.createdDate || item.updatedAt || "",
});

const sortOldestFirst = (a, b) => {
  const dateA = new Date(a.rawDate).getTime();
  const dateB = new Date(b.rawDate).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateA - dateB;
  }

  return Number(a.id || 0) - Number(b.id || 0);
};

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [prevItem, setPrevItem] = useState(null);
  const [nextItem, setNextItem] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;

      try {
        setLoading(true);

        const result = await getDataDetail("delegate", id);
        const files = getFiles(result);

        setItem({
          id:
            result.id ||
            result.certificateDocId ||
            result.delegateDocId ||
            result.docId ||
            id,
          title: result.title || "",
          writer:
            result.writer ||
            result.userName ||
            result.createdBy ||
            "총학생회",
          date: formatDate(result.date || result.createdAt),
          view: result.viewer ?? result.view ?? 0,
          content: result.content || "",
          files,
        });

        const listResult = await getDataList({
          category: "delegate",
          searchCategory: "TITLE",
          keyWord: "",
          page: 0,
          size: 100,
        });

        const list = getPagedArray(listResult)
          .map(normalizeListItem)
          .filter((listItem) => listItem.id)
          .sort(sortOldestFirst);

        const currentIndex = list.findIndex(
          (listItem) => String(listItem.id) === String(id)
        );

        setPrevItem(currentIndex > 0 ? list[currentIndex - 1] : null);

        setNextItem(
          currentIndex >= 0 && currentIndex < list.length - 1
            ? list[currentIndex + 1]
            : null
        );
      } catch (error) {
        console.error("대의원 활동 인증서 상세 조회 실패:", error);
        setItem(null);
        setPrevItem(null);
        setNextItem(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  if (loading) {
    return <div className="delegate-detail-not-found">불러오는 중입니다.</div>;
  }

  if (!item) {
    return (
      <div className="delegate-detail-not-found">
        게시글을 찾을 수 없습니다.
      </div>
    );
  }

  const files = item.files || [];

  return (
    <div className="delegate-detail-wrap">
      <div className="delegate-detail-inner">
        <div className="delegate-detail-title">{item.title}</div>

        <div className="delegate-detail-meta-author">
          <span className="delegate-detail-author-name">{item.writer}</span>
        </div>

        <div className="delegate-detail-meta-row">
          <span>
            <span className="delegate-detail-label">작성일</span> {item.date}
          </span>
          <span>
            <span className="delegate-detail-label">조회수</span> {item.view}
          </span>
        </div>

        <div className="delegate-detail-divider" />

        <div className="delegate-detail-body">
          {(item.content || "").split("\n").map((line, i) => (
            <p key={i}>{line || "\u00A0"}</p>
          ))}
        </div>

        {files.length > 0 && (
          <div className="delegate-detail-attachments">
            {files.map((file, index) => (
              <div
                className="delegate-detail-attachment"
                key={file.id || `${file.name}-${index}`}
              >
                <span className="delegate-detail-attach-name">
                  {file.name}
                  {file.size ? ` (${file.size})` : ""}
                </span>

                {file.url ? (
                  <a
                    className="delegate-detail-attach-btn"
                    href={file.url}
                    rel="noopener noreferrer"
                    download={file.name || "첨부파일"}
                  >
                    ↓
                  </a>
                ) : (
                  <button className="delegate-detail-attach-btn" type="button">
                    ↓
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="delegate-detail-nav">
          <div
            className={`delegate-detail-nav-item ${
              !nextItem ? "disabled" : ""
            }`}
            onClick={() =>
              nextItem && navigate(`/documents/delegate/${nextItem.id}`)
            }
          >
            <span className="delegate-detail-nav-arrow">∧</span>
            <span className="delegate-detail-nav-text">
              {nextItem ? nextItem.title : "다음 글이 없습니다"}
            </span>
          </div>

          <div
            className={`delegate-detail-nav-item ${
              !prevItem ? "disabled" : ""
            }`}
            onClick={() =>
              prevItem && navigate(`/documents/delegate/${prevItem.id}`)
            }
          >
            <span className="delegate-detail-nav-arrow">∨</span>
            <span className="delegate-detail-nav-text">
              {prevItem ? prevItem.title : "이전 글이 없습니다"}
            </span>
          </div>
        </div>

        <div className="delegate-detail-back-row">
          <button
            className="delegate-detail-back-btn"
            onClick={() => navigate("/documents/delegate")}
            type="button"
          >
            목록
          </button>
        </div>
      </div>
    </div>
  );
}